import axios from 'axios';
import { useEffect, useState } from 'react';
import { Button, Dropdown, Form, InputGroup } from 'react-bootstrap';
import { countryOptions } from './Countries';


export default function LocationPicker({
  companyName,
  apiUrl,
  initialCountry = 'IN',
  initialSelection = [],
  onChange,
}) {
  const [country, setCountry] = useState(initialCountry);
  const [locations, setLocations] = useState([]);
  const [selected, setSelected] = useState(initialSelection);
  const [manualLocation, setManualLocation] = useState('');
  const [showAddInput, setShowAddInput] = useState(false);
  const [customCountry, setCustomCountry] = useState('');

  useEffect(() => {
    setSelected(initialSelection);
  }, [initialSelection]);

  useEffect(() => {
    if (!companyName) return;
    axios
      .get(`${apiUrl}/getLocationsForCompany`, {
        params: {
          companyName,
          countryCode: (country === 'OTHER' ? customCountry : country).toLowerCase(),
        },
      })
      .then(res => {
        setLocations(res.data || []);
        setShowAddInput(false); // Reset manual input visibility when locations reload
        setManualLocation('');
      })
      .catch(console.error);
  }, [companyName, country, apiUrl]);

  const toggle = loc => {
    const next = selected.includes(loc)
      ? selected.filter(l => l !== loc)
      : [...selected, loc];
    setSelected(next);
    onChange(next, country);
  };

  const handleAddLocation = () => {
    if (manualLocation.trim() === '') return;

    // Split input by commas, trim each location, and remove empty values
    const newLocations = manualLocation
      .split(',')
      .map(loc => loc.trim())
      .filter(loc => loc !== '');

    if (newLocations.length === 0) return;

    // Avoid duplicates in selected/locations
    const newSelected = [...new Set([...selected, ...newLocations])];
    const updatedLocations = [...new Set([...locations, ...newLocations])];
    const effectiveCountry = country === 'OTHER' ? customCountry : country;

    setSelected(newSelected);
    setLocations(updatedLocations);
    onChange(newSelected, effectiveCountry);

    setManualLocation('');
  };


  return (
    <>

      <Form.Group controlId="locationGroup">
        <Form.Label className="required-label">Location</Form.Label>
        <InputGroup className="mb-4">
          {/* Country dropdown */}
          <Form.Select
            value={country}
            onChange={e => {
              const c = e.target.value;
              setCountry(c);
              setManualLocation('');
              setSelected([]);
              setLocations([]);
              setCustomCountry('');
              if (c !== 'OTHER' && initialSelection.length) onChange([], c);
            }}
            style={{ maxWidth: '5rem' }}
          >
            {countryOptions.map(cn => (
              <option key={cn.code} value={cn.code}>
                {cn.code}  {/* Show country code here */}
              </option>
            ))}
            <option value="OTHER">Other</option>
          </Form.Select>
          {/* If 'Other' selected, show manual country code input */}
          {country === 'OTHER' && (
            <Form.Control
              type="text"
              placeholder="Enter country code"
              value={customCountry}
              maxLength={2}  // country codes are generally 2 letters
              onChange={e => setCustomCountry(e.target.value.toUpperCase())}
              style={{ maxWidth: '6rem', marginLeft: '0.5rem' }}
            />
          )}
          {/* If locations exist, show dropdown; else show input beside country */}
          {locations.length > 0 ? (
            <Dropdown>
              <Dropdown.Toggle
                id="location-dropdown"
                style={{
                  flex: 1,
                  textAlign: 'left',
                  backgroundColor: 'white',
                  color: 'black',
                  borderColor: '#ced4da',
                }}
              >
                {selected.length ? selected.join(', ') : 'Select location(s)…'}
              </Dropdown.Toggle>
              <Dropdown.Menu
                style={{ maxHeight: '200px', overflowY: 'auto', minWidth: '18rem' }}
              >
                {locations.map(loc => (
                  <Form.Check
                    key={loc}
                    type="checkbox"
                    id={`loc-${loc}`}
                    label={loc}
                    checked={selected.includes(loc)}
                    onChange={() => toggle(loc)}
                    style={{ padding: '0.25rem 2rem' }}
                  />
                ))}
              </Dropdown.Menu>
            </Dropdown>
          ) : (
            <Form.Control
              type="text"
              placeholder="Enter location"
              value={manualLocation}
              onChange={e => setManualLocation(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddLocation();
                }
              }}
            />
          )}

          {/* Show Add button only when locations are not available */}
          {locations.length === 0 && (
            <Button variant="primary" onClick={handleAddLocation}>
              Add
            </Button>
          )}
        </InputGroup>
      </Form.Group>
      {/* Move message outside the Form */}
      {locations.length === 0 && (
        <p style={{ marginBottom: '0.5rem', color: '#666' }}>
          No location found, please add location manually.
        </p>
      )}
    </>
  );


}
